<template>
    <a
        v-if="languagesList.length > 0"
        class="the-language-switcher"
        id="vc-header-switch-lang"
        @click="onClickOfSwitchLanguagesLink()"
        @keyup.enter="onClickOfSwitchLanguagesLink()"
        :href="switchLanguagesURL"
        tabindex="0"
    >
        {{ switchLanguagesText }}
        <KatIcon v-if="multipleAvailableLanguages" class="the-language-switcher-icon" name="chevron-down" size="tiny" />
        <TheSwitchLanguageDropdown v-if="showSwitchLanguagesModal" :languagesList="languagesList" />
    </a>
</template>

<script>
import { mapState } from 'pinia';
import { useContextStore } from '../stores/contextStore';
import { useLanguagesStore } from '../stores/languagesStore';
import { getSwitchLanguageString, getSwitchLanguageURL } from '../util/switchLanguage';
import KatIcon from './katal/KatIcon';
import TheSwitchLanguageDropdown from './TheSwitchLanguageDropdown.vue';

export default {
    name: 'TheLanguageSwitcher',
    components: {
        KatIcon,
        TheSwitchLanguageDropdown,
    },
    data: function () {
        return {
            showSwitchLanguagesModal: false,
        };
    },
    computed: {
        ...mapState(useLanguagesStore, ['localeCodes']),
        ...mapState(useContextStore, ['marketscope']),
        currentLanguage() {
            return this.$locale.replace('-', '_');
        },
        languagesList() {
            // Remove currently selected locale from list of languages to switch to
            return this.localeCodes?.filter((lang) => lang != this.currentLanguage) || [];
        },
        oneAvailableLanguage() {
            return this.languagesList.length == 1;
        },
        multipleAvailableLanguages() {
            return this.languagesList.length > 1;
        },
        switchLanguagesURL() {
            return this.oneAvailableLanguage ? getSwitchLanguageURL(this.languagesList[0]) : undefined;
        },
        switchLanguagesText() {
            if (this.oneAvailableLanguage) {
                const locale = this.languagesList[0];
                const text = getSwitchLanguageString(locale);
                if (!text) {
                    this.$metrics
                        .newChildActionPublisherForMethod('SwitchLanguage')
                        .publishCounterMonitor(`missingLabel.${locale}`, 1);
                }
                return text || `View this site in ${locale}`;
            } else {
                return this.$t('header_center_switchLanguages');
            }
        },
    },
    created() {
        useLanguagesStore().loadLanguages();
        useContextStore().loadContext();
    },
    methods: {
        onClickOfSwitchLanguagesLink() {
            if (this.multipleAvailableLanguages) {
                this.showSwitchLanguagesModal = !this.showSwitchLanguagesModal;
                this.$metrics
                    .newChildActionPublisherForMethod('SwitchLanguage')
                    .publishCounterMonitor(this.showSwitchLanguagesModal ? 'opened' : 'closed', 1);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.the-language-switcher {
    align-items: center;
    display: flex;
    position: relative;

    &-icon {
        margin-left: 4px; // 8px after accounting for whitespace in icon
        margin-right: -4px; // Subtracting out whitespace in icon
        filter: invert(29%) sepia(22%) saturate(4547%) hue-rotate(160deg) brightness(98%) contrast(101%); // Using filter to recolor the SVG to #007185, see https://stackoverflow.com/a/59411103
    }

    &:hover &-icon {
        filter: invert(34%) sepia(82%) saturate(994%) hue-rotate(346deg) brightness(92%) contrast(92%); // Using filter to recolor the SVG to #C7511F, see https://stackoverflow.com/a/59411103
    }
}
</style>
